import { Link } from '@tanstack/react-router'
import { type SearchHit, type SearchHitKind, taskRef } from '@/entities/task'
import { cn } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/icon'
import { Snippet } from '@/shared/ui/snippet'
import { matchedInLabel } from '../lib/matched-in'

interface HitRowProps {
  hit: SearchHit
  active?: boolean
  onSelect?: () => void
}

// Тип сущности в строке — иконка + подпись, чтобы «откуда найдено» было видно.
const KIND_META: Record<SearchHitKind, { label: string; icon: IconName }> = {
  task: { label: 'Задача', icon: 'check' },
  message: { label: 'Сообщение', icon: 'reply' },
  company: { label: 'Компания', icon: 'building' },
  project: { label: 'Проект', icon: 'kanban' },
  board: { label: 'Доска', icon: 'gantt' },
}

// Совпадение в имени (title/name) самоочевидно — snippet дублировал бы заголовок.
const NAME_MATCH = new Set(['title', 'name'])

// Строка выдачи поиска (палитра + страница /search): бейдж типа сущности · (#ref
// для задачи/сообщения) · заголовок · метка «почему в выдаче»; ниже — snippet.
// Навигация — по kind к соответствующему роуту (url ответа = источник истины).
export function HitRow({ hit, active = false, onSelect }: HitRowProps) {
  const badge = matchedInLabel(hit.matchedIn)
  const meta = KIND_META[hit.kind]
  const isTaskLike = hit.kind === 'task' || hit.kind === 'message'
  const className = cn(
    'block rounded-control px-3 py-2 transition-colors',
    active ? 'bg-bg-elevated' : 'hover:bg-bg-elevated',
  )

  const inner = (
    <>
      <div className="flex items-center gap-2">
        <span
          className="inline-flex shrink-0 items-center gap-1 text-xs text-text-tertiary"
          title={meta.label}
        >
          <Icon name={meta.icon} size={12} />
        </span>
        {isTaskLike && (
          <span data-numeric className="mono-num shrink-0 text-xs text-text-tertiary">
            {taskRef(hit.id)}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-text-primary">{hit.title}</span>
        {badge && (
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-text-tertiary">
            {badge.icon && <Icon name={badge.icon} size={12} />}
            {badge.label}
          </span>
        )}
      </div>
      {hit.snippet && !NAME_MATCH.has(hit.matchedIn) && (
        <Snippet text={hit.snippet} className="mt-0.5 line-clamp-1 text-xs" />
      )}
    </>
  )

  if (isTaskLike) {
    return (
      <Link
        to="/tasks/$taskId"
        params={{ taskId: hit.id }}
        onClick={onSelect}
        className={className}
      >
        {inner}
      </Link>
    )
  }
  if (hit.kind === 'company') {
    return (
      <Link
        to="/companies/$companyId"
        params={{ companyId: hit.id }}
        onClick={onSelect}
        className={className}
      >
        {inner}
      </Link>
    )
  }
  if (hit.kind === 'board') {
    return (
      <Link
        to="/boards/$boardId"
        params={{ boardId: hit.id }}
        onClick={onSelect}
        className={className}
      >
        {inner}
      </Link>
    )
  }
  // project — своей страницы нет, открываем в контексте его компании (companyId из
  // ответа); без компании — хаб компаний.
  return hit.companyId ? (
    <Link
      to="/companies/$companyId"
      params={{ companyId: hit.companyId }}
      onClick={onSelect}
      className={className}
    >
      {inner}
    </Link>
  ) : (
    <Link to="/companies" onClick={onSelect} className={className}>
      {inner}
    </Link>
  )
}
